import {Button, Dialog, DialogPanel, DialogTitle} from '@headlessui/react'
import {useRouteLoaderData} from '@remix-run/react'
import type {SyntheticEvent} from 'react'
import {useEffect, useState} from 'react'
import {useReportWidgetClick, useReportWidgetSeen} from '~/hooks/analytic-hooks'
import {BrgIconClose} from '~/icons/brg-icon-close'
import {getTranslation} from '~/localization/translation'
import type {RootLoaderResponse} from '~/types'

export default function HustonProblemModal({timeThresholdInSec}: {timeThresholdInSec: number}) {
  const {language} = useRouteLoaderData('root') as RootLoaderResponse
  const translations = getTranslation(language)
  const reportClick = useReportWidgetClick()
  const [isOpen, setIsOpen] = useState(false)
  const [regularRedirectLink, setRegularRedirectLink] = useState<string | undefined>(undefined)
  const [toSiteTimeSec, setToSiteTimeSec] = useState(0)

  const imgSrc = 'https://dt47coepsakuj.cloudfront.net/images/huston-problem-2.webp'

  useEffect(() => {
    const redirectUrl = localStorage.getItem('redirectUrl') ?? ''
    if (typeof Storage != 'undefined' && localStorage.getItem('redirectUrl')) {
      const [basePath, queryString] = redirectUrl.split('?')
      const searchParams = new URLSearchParams(queryString)
      searchParams.append('use_amazon_regular_link', 'true')
      setRegularRedirectLink(`${basePath}?${searchParams.toString()}`)
    }
  }, [isOpen])

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>

    const handleFocus = () => {
      if (document.visibilityState === 'visible' && localStorage?.getItem('isEligibleForSiteReturnEvent')) {
        const clickTime = localStorage?.getItem('clickTime') ?? '0'
        const now = Date.now()
        const elapsedTime = Math.round((now - parseInt(clickTime, 10)) / 1000)

        if (elapsedTime <= timeThresholdInSec) {
          setToSiteTimeSec(elapsedTime)
          setIsOpen(true)
        }
      }

      timer = setTimeout(() => {
        if (localStorage?.getItem('clickTime')) {
          localStorage?.setItem('isEligibleForSiteReturnEvent', 'true')
        }
      }, 500)
    }
    const handleBeforeUnload = () => {
      localStorage.removeItem('clickTime')
      localStorage.removeItem('isEligibleForSiteReturnEvent')
      localStorage.removeItem('redirectUrl')
    }

    document.addEventListener('visibilitychange', handleFocus)
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.removeEventListener('focus', handleFocus)
    handleFocus()

    return () => {
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', handleFocus)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [timeThresholdInSec])

  const [modalRef] = useReportWidgetSeen({
    widget_name: 'huston-problem-modal',
    widget_place: 'full-screen',
    variation: JSON.stringify({toSiteTimeSec}),
  })

  function handleCLoseModal() {
    reportClick({
      widget_name: 'close',
      widget_place: 'huston-problem-modal',
      variation: JSON.stringify({toSiteTimeSec}),
    })
    setIsOpen(false)
  }

  function handleAmazonClickCta(e: SyntheticEvent) {
    reportClick({
      widget_name: 'go-to-amazon-cta',
      widget_place: 'huston-problem-modal',
      variation: JSON.stringify({toSiteTimeSec}),
    })
    e.preventDefault()
    e.stopPropagation()
    setIsOpen(false)
    window.open(regularRedirectLink, '_blank')
  }

  function handleBackToListCta() {
    reportClick({
      widget_name: 'back-to-list-cta',
      widget_place: 'huston-problem-modal',
      variation: JSON.stringify({toSiteTimeSec}),
    })
    setIsOpen(false)
  }

  return (
    <>
      <Dialog open={isOpen} onClose={() => setIsOpen(false)} className="relative z-50 h-screen">
        <div className="fixed inset-0 flex h-screen w-screen items-center justify-center">
          <DialogPanel
            className="flex h-screen max-w-full flex-col items-center justify-center gap-4 border bg-white p-4 text-center"
            ref={modalRef}
          >
            <button type="button" aria-label="close window" onClick={handleCLoseModal}>
              <BrgIconClose className="absolute right-5 top-5 fill-black" />
            </button>
            <img alt="huston-problem" src={imgSrc} className="h-[200px] w-[294px] object-scale-down" />
            <DialogTitle as="h3" className="text-4xl font-bold text-black">
              {translations.HustonProblemModal.title}
            </DialogTitle>
            <div className="text-[18px] font-light text-black">
              <p>{translations.HustonProblemModal.subtitle_p1}</p>
              <p>{translations.HustonProblemModal.subtitle_p2}</p>
            </div>
            <Button
              className="w-full max-w-[75%] rounded-xl border border-orange-500 bg-orange-500 px-3 py-3 text-base text-white"
              onClick={handleAmazonClickCta}
            >
              {translations.HustonProblemModal.goToAmazonCta}
            </Button>
            <Button
              className="w-full max-w-[75%] rounded-xl border border-orange-500 px-3 py-3 text-base text-orange-500"
              onClick={handleBackToListCta}
            >
              {translations.HustonProblemModal.backToListCta}
            </Button>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  )
}
